import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useEffect, useMemo, useState } from "react";
import { externalService, ExternalMovie } from "@/services/externalService";
import { callCreatePostBulk } from "@/services/ophim69Service";
import { useDebounce } from "@/hooks/useDebounce";
import { showToast } from "@/lib/toast";
import { Search, RefreshCw, Film, Plus,  } from "lucide-react";

interface ModalAddPostProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}

const DEFAULT_DOMAIN_ID = "68e4f8a2e44196de5db161b6";

export default function ModalAddPost({
  isOpen,
  onClose,
  onCompleted,
}: ModalAddPostProps) {
  const [movies, setMovies] = useState<ExternalMovie[]>([]);
  const [selectedMovieIds, setSelectedMovieIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debounced = useDebounce(search, 400);

  useEffect(() => {
    if (!isOpen) return;
    const run = async () => {
      setIsLoading(true);
      try {
        const res: any = await externalService.getAllMovies();
        console.log(res);
        const all = res?.data || res; // apiClient shape
        const list: ExternalMovie[] = all?.data?.data || [];
        setMovies(list);
      } finally {
        setIsLoading(false);
      }
    };
    run();
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!debounced) return movies;
    const s = debounced.toLowerCase();
    return movies.filter(
      (m) =>
        (m.name || "").toLowerCase().includes(s) ||
        String(m.year || "").includes(s)
    );
  }, [movies, debounced]);

  // Sidebar-like selector UI

  const handleSubmit = async () => {
    if (selectedMovieIds.length === 0) {
      showToast.error("Vui lòng chọn ít nhất 1 phim");
      return;
    }
    setIsSubmitting(true);
    try {
      const res: any = await callCreatePostBulk({
        domain_ids: [DEFAULT_DOMAIN_ID],
        movie_ids: selectedMovieIds,
      });
      if (res) {
        showToast.success("Tạo bài viết thành công");
        onCompleted?.();
        onClose();
        setSelectedMovieIds([]);
      } else {
        showToast.error("Tạo bài viết thất bại");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-6 flex-1 min-h-0 flex flex-col">
          <DialogHeader>
            <DialogTitle>Thêm mới bài viết</DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="relative mb-4 mt-5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Tìm kiếm phim theo tên hoặc năm..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* List */}
          <div className="flex-1 min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-500">
                  Đang tải danh sách phim...
                </span>
              </div>
            ) : (
              <div className="h-full overflow-y-auto scrollbar-gentle">
                {filtered.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <Film className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">Không có phim nào</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 p-2 overflow-y-auto max-h-[500px] scrollbar-gentle">
                    {filtered.map((m: any) => {
                      const isSelected = selectedMovieIds.includes(m._id);
                      return (
                        <div
                          key={m._id}
                          className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                            isSelected
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          onClick={() => {
                            setSelectedMovieIds((prev) =>
                              prev.includes(m._id)
                                ? prev.filter((id) => id !== m._id)
                                : [...prev, m._id]
                            );
                          }}
                        >
                          <div className="flex gap-3 items-center">
                            {m?.posters[0] ? (
                              <img
                                src={
                                  "https://api.ophim69.com/api/v1/" +
                                  m?.posters[0]
                                }
                                alt={m.name}
                                className="w-10 h-14 bg-gray-100 rounded"
                              />
                            ) : (
                              <div className="w-10 h-14 bg-gray-100 rounded" />
                            )}
                            <div
                              className="flex-1 min-w-0"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src =
                                  "/placeholder-movie.jpg";
                              }}
                            >
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {m.name}
                              </h4>
                              {m.year && (
                                <p className="text-xs text-gray-500">
                                  Năm: {m.year}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between mt-4 items-center">
            <div className="text-sm text-gray-500">
              Đã chọn: {selectedMovieIds.length} phim
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Hủy
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || selectedMovieIds.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                Thêm {selectedMovieIds.length} phim
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
